/**
 * Detect incomplete / dropped Agent247 calls for dashboard messaging.
 * Mirrors backend assessTranscriptCompleteness (transcriptTurns.js) at a high level.
 */

const isAgentRole = (role) => {
  const v = String(role || "").toLowerCase();
  return v.includes("agent") || v.includes("assistant") || v.includes("kathy");
};

export function resolveAgent247TurnMessage(row = {}) {
  const spoken = String(row?.text || row?.message || "").trim();
  const original = String(row?.original_message || row?.originalMessage || "").trim();
  if (row?.interrupted === true && original) {
    return original.length >= spoken.length ? original : spoken || original;
  }
  if (spoken && original && spoken.length <= 4 && original.length > spoken.length + 8) {
    return original;
  }
  return spoken || original;
}

const isTruncatedAgentMessage = (message = "") => {
  const text = String(message || "").trim();
  if (!text) return true;
  if (text.length <= 3) return true;
  if (/^I(\s*\.\.\.|\.{1,3})?$/i.test(text)) return true;
  if (/\.\.\.$/.test(text) && text.length < 40) return true;
  return false;
};

function readTerminationReason(payload = {}) {
  return String(
    payload?.normalized?.terminationReason ||
      payload?.data?.metadata?.termination_reason ||
      payload?.metadata?.termination_reason ||
      "",
  ).trim();
}

function readGuardrailInfo(payload = {}, termination = "") {
  if (payload?.normalized?.guardrailStopped) {
    return {
      stopped: true,
      name: String(payload?.normalized?.guardrailName || "").trim(),
      termination: termination || readTerminationReason(payload),
    };
  }
  const term = termination || readTerminationReason(payload);
  if (!term.toLowerCase().includes("guardrail")) return null;
  const quoted = term.match(/'([^']+)'/);
  return {
    stopped: true,
    name: quoted?.[1] || "",
    termination: term,
  };
}

export function assessAgent247TranscriptHealth(transcriptRows = [], payload = {}) {
  const rows = Array.isArray(transcriptRows) ? transcriptRows : [];
  const reasons = [];
  const termination = readTerminationReason(payload);
  const guardrail = readGuardrailInfo(payload, termination);

  if (guardrail?.stopped) {
    const label = guardrail.name || "a platform guardrail";
    return {
      incomplete: true,
      guardrail: true,
      reasons: ["guardrail_triggered"],
      banner:
        `ElevenLabs ended this call early: the “${label}” guardrail triggered after about ` +
        `90 seconds of intake. Home care calls normally mention health, hospital discharge, and care needs — ` +
        `that is expected, not a compliance violation. Disable or relax this guardrail on the “24/7 Call Assist - Unity” ` +
        `agent in ElevenLabs, then call the family back to finish intake.`,
    };
  }

  if (payload?.normalized?.transcriptIncomplete === true) {
    reasons.push("stored_incomplete");
  }

  if (!rows.length) {
    return {
      incomplete: true,
      guardrail: false,
      banner:
        "This call has no transcript. The caller may have hung up before Kathy could capture details — follow up by phone.",
      reasons: reasons.length ? reasons : ["empty_transcript"],
    };
  }

  const last = rows[rows.length - 1];
  if (isAgentRole(last?.role) && isTruncatedAgentMessage(last?.message)) {
    reasons.push("agent_cut_off");
  }

  const termLower = termination.toLowerCase();
  if (termLower.includes("error") || termLower.includes("failed")) {
    reasons.push("termination");
  }

  const incomplete = reasons.length > 0;
  let banner = "";
  if (incomplete) {
    banner =
      "This call ended before Kathy finished responding (network hang-up, caller disconnect, or line drop). " +
      "The transcript shows what was captured — please call the family back to complete intake.";
  }

  return { incomplete, guardrail: false, banner, reasons };
}
